/**
 * Super admin — create, view, and respond to customer support tickets.
 */
import { useEffect, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import swal from 'sweetalert';
import { API_URL } from '../../../constant/API_Settings';
import { fetchPlatformSuperAdminProfile } from '../../../utils/platformAuth';
import { fetchAdminPumpDetail, fetchAdminPumps } from './adminApi';
import Sidebar_Progress from '../../layouts/Admin_Sidebar_Progress.component';
import LoginHeaderOperational from '../LoginHeaderOperational';
import {
  adminCreateSupportTicket,
  adminDeleteSupportAttachment,
  adminReplySupportTicket,
  adminUpdateSupportTicketStatus,
  downloadSupportAttachment,
  fetchAdminSupportTicketDetail,
  fetchAdminSupportTickets,
  fetchSupportMeta,
  saveBlobAsFile,
} from '../../support/supportApi';
import '../../support/help-support.css';
import './admin-dashboard.css';

const EMPTY_CREATE = {
  pump_id: '',
  user_id: '',
  subject: '',
  description: '',
  category: 'complaint',
  priority: 'medium',
};

function StatusBadge({ status, label }) {
  return <span className={`help-status-badge help-status-${status}`}>{label || status}</span>;
}

function formatBytes(size) {
  const n = Number(size || 0);
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

export default function AdminSupportCenter(props) {
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState({});
  const [pagination, setPagination] = useState({});
  const [meta, setMeta] = useState({ statuses: [], categories: [], priorities: [] });
  const [filters, setFilters] = useState({ status: '', priority: '', category: '', search: '' });
  const [selected, setSelected] = useState(null);
  const [reply, setReply] = useState('');
  const [replyStatus, setReplyStatus] = useState('waiting_customer');
  const [sending, setSending] = useState(false);

  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState(EMPTY_CREATE);
  const [pumps, setPumps] = useState([]);
  const [pumpUsers, setPumpUsers] = useState([]);
  const [pumpSearch, setPumpSearch] = useState('');
  const [loadingPumps, setLoadingPumps] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    fetchPlatformSuperAdminProfile(API_URL, localStorage.getItem('AUTH_TOKEN'))
      .then((profile) => (profile ? loadList() : history.goBack()))
      .catch(() => history.goBack());
    fetchSupportMeta().then(setMeta).catch(() => {});
  }, [history]);

  function loadList(page = 1) {
    setLoading(true);
    fetchAdminSupportTickets({ ...filters, page })
      .then((res) => {
        setTickets(res.tickets || []);
        setStats(res.stats || {});
        setPagination(res.pagination || {});
      })
      .catch((err) => swal('Error', err.message, 'error'))
      .finally(() => setLoading(false));
  }

  function openCreate() {
    setShowCreate(true);
    setSelected(null);
    setCreateForm(EMPTY_CREATE);
    setPumpUsers([]);
    setPumpSearch('');
    loadPumps('');
  }

  function loadPumps(search) {
    setLoadingPumps(true);
    fetchAdminPumps(search || '', 1)
      .then((res) => setPumps(res.data || []))
      .catch(() => setPumps([]))
      .finally(() => setLoadingPumps(false));
  }

  function onPumpChange(pumpId) {
    setCreateForm((f) => ({ ...f, pump_id: pumpId, user_id: '' }));
    setPumpUsers([]);
    if (!pumpId) return;
    setLoadingUsers(true);
    fetchAdminPumpDetail(pumpId)
      .then((res) => {
        const users = (res.users || []).filter((u) => Number(u.status) !== 2);
        setPumpUsers(users);
        if (users.length === 1) {
          setCreateForm((f) => ({ ...f, user_id: String(users[0].id) }));
        }
      })
      .catch(() => setPumpUsers([]))
      .finally(() => setLoadingUsers(false));
  }

  function submitCreate(e) {
    e.preventDefault();
    if (!createForm.pump_id) {
      swal('Missing pump', 'Select a pump for this ticket.', 'warning');
      return;
    }
    setCreating(true);
    adminCreateSupportTicket({
      pump_id: Number(createForm.pump_id),
      user_id: createForm.user_id ? Number(createForm.user_id) : undefined,
      subject: createForm.subject.trim(),
      description: createForm.description.trim(),
      category: createForm.category,
      priority: createForm.priority,
    })
      .then((res) => {
        swal('Created', res.message || 'Ticket created', 'success');
        setShowCreate(false);
        setCreateForm(EMPTY_CREATE);
        loadList(1);
        if (res.ticket?.id) {
          openTicket(res.ticket.id);
        }
      })
      .catch((err) => swal('Error', err.message, 'error'))
      .finally(() => setCreating(false));
  }

  function openTicket(id) {
    setShowCreate(false);
    fetchAdminSupportTicketDetail(id)
      .then((res) => {
        if (res.ticket) {
          setSelected(res.ticket);
          setReplyStatus(res.ticket.status === 'open' ? 'waiting_customer' : res.ticket.status);
        }
      })
      .catch((err) => swal('Error', err.message, 'error'));
  }

  function sendReply(e) {
    e.preventDefault();
    if (!selected || reply.trim().length < 3) return;
    setSending(true);
    adminReplySupportTicket(selected.id, reply.trim(), replyStatus)
      .then((res) => {
        swal('Sent', res.message || 'Reply sent', 'success');
        setReply('');
        setSelected(res.ticket || selected);
        loadList(pagination.current_page || 1);
      })
      .catch((err) => swal('Error', err.message, 'error'))
      .finally(() => setSending(false));
  }

  function changeStatus(status) {
    if (!selected) return;
    adminUpdateSupportTicketStatus(selected.id, status)
      .then((res) => {
        setSelected(res.ticket || selected);
        loadList(pagination.current_page || 1);
      })
      .catch((err) => swal('Error', err.message, 'error'));
  }

  function downloadAttachment(attachment) {
    if (!selected) return;
    downloadSupportAttachment(selected.id, attachment.id, true)
      .then(({ blob, filename }) => saveBlobAsFile(blob, filename || attachment.original_name))
      .catch((err) => swal('Error', err.message, 'error'));
  }

  function deleteAttachment(attachment) {
    if (!selected) return;
    swal({
      title: 'Delete attachment?',
      text: `${attachment.original_name} will be permanently removed.`,
      icon: 'warning',
      buttons: true,
      dangerMode: true,
    }).then((ok) => {
      if (!ok) return;
      adminDeleteSupportAttachment(selected.id, attachment.id)
        .then((res) => {
          setSelected(res.ticket || selected);
          swal('Deleted', res.message || 'Attachment deleted', 'success');
        })
        .catch((err) => swal('Error', err.message, 'error'));
    });
  }

  return (
    <div className="main-container page-wrap admin-platform">
      <div className="d-md-flex">
        <Sidebar_Progress props={props} progress={30} />
        <div className="content-area">
          <LoginHeaderOperational props={props} page_title="Support Tickets" />
          <div className="container pb-3 mt-3">
            <div className="d-flex flex-wrap justify-content-between align-items-start gap-2">
              <div>
                <Link to="/admin_dashboard" className="small">← Control Center</Link>
                <h3 className="mt-2 mb-1">Support Tickets</h3>
                <p className="text-muted small mb-0">
                  Create tickets for pumps, or reply to requests from the Help &amp; Support widget.
                </p>
              </div>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => (showCreate ? setShowCreate(false) : openCreate())}
              >
                {showCreate ? 'Cancel' : '+ Create ticket'}
              </button>
            </div>

            <div className="row g-2 mt-3 mb-3">
              {[
                ['open', 'Open', stats.open],
                ['in_progress', 'In progress', stats.in_progress],
                ['waiting_customer', 'Waiting customer', stats.waiting_customer],
                ['resolved', 'Resolved', stats.resolved],
                ['sla_breached', 'SLA breached', stats.sla_breached],
              ].map(([key, label, count]) => (
                <div key={key} className="col-6 col-md">
                  <div className={`admin-panel text-center py-2 ${key === 'sla_breached' && count ? 'border border-danger' : ''}`}>
                    <div className="h4 mb-0">{count ?? 0}</div>
                    <small>{label}</small>
                  </div>
                </div>
              ))}
            </div>

            {showCreate && (
              <div className="admin-panel mb-3">
                <h5 className="mb-3">Create support ticket</h5>
                <form onSubmit={submitCreate}>
                  <div className="row g-2 mb-2">
                    <div className="col-md-6">
                      <label className="form-label small">Search pumps</label>
                      <div className="input-group input-group-sm">
                        <input
                          type="search"
                          className="form-control"
                          placeholder="Pump name…"
                          value={pumpSearch}
                          onChange={(e) => setPumpSearch(e.target.value)}
                        />
                        <button
                          type="button"
                          className="btn btn-outline-secondary"
                          onClick={() => loadPumps(pumpSearch)}
                          disabled={loadingPumps}
                        >
                          {loadingPumps ? '…' : 'Search'}
                        </button>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label small">Pump *</label>
                      <select
                        className="form-select form-select-sm"
                        value={createForm.pump_id}
                        onChange={(e) => onPumpChange(e.target.value)}
                        required
                      >
                        <option value="">Select pump…</option>
                        {pumps.map((p) => (
                          <option key={p.id} value={p.id}>{p.pump_name || `Pump #${p.id}`}</option>
                        ))}
                      </select>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label small">Contact user {loadingUsers ? '(loading…)' : ''}</label>
                      <select
                        className="form-select form-select-sm"
                        value={createForm.user_id}
                        onChange={(e) => setCreateForm((f) => ({ ...f, user_id: e.target.value }))}
                        disabled={!createForm.pump_id || loadingUsers}
                      >
                        <option value="">Auto-select first active user</option>
                        {pumpUsers.map((u) => (
                          <option key={u.id} value={u.id}>
                            {u.name || u.user_name}{u.role?.role_name ? ` (${u.role.role_name})` : ''}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-md-3">
                      <label className="form-label small">Category *</label>
                      <select
                        className="form-select form-select-sm"
                        value={createForm.category}
                        onChange={(e) => setCreateForm((f) => ({ ...f, category: e.target.value }))}
                        required
                      >
                        {(meta.categories || []).map((c) => (
                          <option key={c.id} value={c.id}>{c.label}</option>
                        ))}
                      </select>
                    </div>
                    <div className="col-md-3">
                      <label className="form-label small">Priority</label>
                      <select
                        className="form-select form-select-sm"
                        value={createForm.priority}
                        onChange={(e) => setCreateForm((f) => ({ ...f, priority: e.target.value }))}
                      >
                        {(meta.priorities || []).map((p) => (
                          <option key={p.id} value={p.id}>{p.label}</option>
                        ))}
                      </select>
                    </div>
                    <div className="col-12">
                      <label className="form-label small">Subject *</label>
                      <input
                        className="form-control form-control-sm"
                        value={createForm.subject}
                        onChange={(e) => setCreateForm((f) => ({ ...f, subject: e.target.value }))}
                        minLength={5}
                        required
                        placeholder="Short summary of the issue"
                      />
                    </div>
                    <div className="col-12">
                      <label className="form-label small">Description *</label>
                      <textarea
                        className="form-control form-control-sm"
                        rows={4}
                        value={createForm.description}
                        onChange={(e) => setCreateForm((f) => ({ ...f, description: e.target.value }))}
                        minLength={10}
                        required
                        placeholder="Describe the issue or request for this pump…"
                      />
                    </div>
                  </div>
                  <button type="submit" className="btn btn-primary btn-sm" disabled={creating}>
                    {creating ? 'Creating…' : 'Create ticket'}
                  </button>
                </form>
              </div>
            )}

            <div className="row g-3">
              <div className="col-lg-5">
                <div className="admin-panel">
                  <h6 className="mb-3">Filters</h6>
                  <div className="row g-2 mb-2">
                    <div className="col-6">
                      <select
                        className="form-select form-select-sm"
                        value={filters.status}
                        onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
                      >
                        <option value="">All statuses</option>
                        {(meta.statuses || []).map((s) => (
                          <option key={s.id} value={s.id}>{s.label}</option>
                        ))}
                      </select>
                    </div>
                    <div className="col-6">
                      <input
                        type="search"
                        className="form-control form-control-sm"
                        placeholder="Search ticket, subject…"
                        value={filters.search}
                        onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
                      />
                    </div>
                  </div>
                  <button type="button" className="btn btn-primary btn-sm mb-3" onClick={() => loadList(1)}>
                    Apply filters
                  </button>

                  {loading ? <p>Loading…</p> : tickets.length === 0 ? (
                    <p className="text-muted">No tickets found.</p>
                  ) : (
                    <ul className="help-ticket-list">
                      {tickets.map((t) => (
                        <li
                          key={t.id}
                          className={selected?.id === t.id ? 'border-primary' : ''}
                          onClick={() => openTicket(t.id)}
                        >
                          <div className="d-flex justify-content-between">
                            <strong>{t.ticket_no}</strong>
                            <StatusBadge status={t.status} label={t.status_label} />
                          </div>
                          <div className="small">{t.pump_name} · {t.user_name}</div>
                          <div className="small mt-1">{t.subject}</div>
                          <div className="small text-muted mt-1">
                            <span className={`help-priority-${t.priority}`}>{t.priority_label}</span>
                            {' · '}{t.category_label}
                            {t.sla?.sla_breached ? <span className="text-danger ms-2">SLA breached</span> : null}
                            {t.attachment_count > 0 ? <span className="ms-2">📎 {t.attachment_count}</span> : null}
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>

              <div className="col-lg-7">
                <div className="admin-panel min-vh-50">
                  {!selected ? (
                    <p className="text-muted">
                      {showCreate
                        ? 'Fill the form above to create a ticket, or select an existing ticket.'
                        : 'Select a ticket to view and respond, or create a new one.'}
                    </p>
                  ) : (
                    <>
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <div>
                          <h5 className="mb-1">{selected.ticket_no}</h5>
                          <p className="mb-0">{selected.subject}</p>
                          <small className="text-muted">
                            {selected.pump_name} · {selected.user_name} ({selected.user_role})
                          </small>
                          {selected.sla?.sla_breached && (
                            <div className="small text-danger mt-1">
                              SLA breached — resolution due {selected.sla.resolution_due_at}
                              {selected.sla.resolution_overdue_hours
                                ? ` (~${selected.sla.resolution_overdue_hours}h overdue)`
                                : ''}
                            </div>
                          )}
                        </div>
                        <StatusBadge status={selected.status} label={selected.status_label} />
                      </div>

                      <p className="small border rounded p-2 bg-light">{selected.description}</p>

                      {(selected.attachments || []).length > 0 && (
                        <div className="mb-3">
                          <div className="small fw-semibold mb-1">Attachments</div>
                          <ul className="list-unstyled small mb-0">
                            {selected.attachments.map((a) => (
                              <li key={a.id} className="d-flex justify-content-between align-items-center border rounded px-2 py-1 mb-1 gap-2">
                                <span className="text-truncate">
                                  {a.original_name} <span className="text-muted">({formatBytes(a.size)})</span>
                                </span>
                                <span className="d-flex gap-2 flex-shrink-0">
                                  <button type="button" className="btn btn-link btn-sm p-0" onClick={() => downloadAttachment(a)}>
                                    Download
                                  </button>
                                  {['resolved', 'closed'].includes(selected.status) && (
                                    <button type="button" className="btn btn-link btn-sm p-0 text-danger" onClick={() => deleteAttachment(a)}>
                                      Delete
                                    </button>
                                  )}
                                </span>
                              </li>
                            ))}
                          </ul>
                          {!['resolved', 'closed'].includes(selected.status) && (
                            <small className="text-muted">Close or resolve the ticket to delete uploaded files.</small>
                          )}
                        </div>
                      )}

                      {(selected.status_history || []).length > 0 && (
                        <div className="mb-3">
                          <div className="small fw-semibold mb-1">Status history</div>
                          <ul className="small text-muted mb-0">
                            {selected.status_history.map((h) => (
                              <li key={h.id}>
                                {h.created_at}: {h.from_status_label || '—'} → <strong>{h.to_status_label}</strong>
                                {h.changed_by_name ? ` · ${h.changed_by_name}` : ''}
                                {h.note ? ` · ${h.note}` : ''}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      <div className="help-message-thread mb-3">
                        {/*
                          The backend always seeds `messages[0]` with the ticket-creation
                          message, whose body is a copy of `ticket.description` (see
                          SupportTicketService::createTicket / adminCreateTicket). That
                          description is already shown above in the ticket summary, so
                          re-rendering it here as a chat bubble would duplicate it. Skip
                          only that leading entry when its text still matches the
                          description; any later entry (genuine replies/comments) keeps
                          rendering normally even if a message happens to share index 0
                          after this one is filtered out of the array.
                        */}
                        {(selected.messages || [])
                          .filter((m, index) => !(index === 0 && m.message === selected.description))
                          .map((m) => (
                            <div key={m.id} className={`help-message-bubble ${m.author_type}`}>
                              <div className="bubble-inner">{m.message}</div>
                              <div className="help-message-meta">{m.author_name} · {m.created_at}</div>
                            </div>
                          ))}
                      </div>

                      <form onSubmit={sendReply}>
                        <label className="form-label small">Reply to customer</label>
                        <textarea
                          className="form-control form-control-sm mb-2"
                          rows={3}
                          value={reply}
                          onChange={(e) => setReply(e.target.value)}
                          placeholder="Write your response…"
                          required
                          minLength={3}
                        />
                        <div className="row g-2 align-items-end">
                          <div className="col-md-5">
                            <label className="form-label small">Set status after reply</label>
                            <select
                              className="form-select form-select-sm"
                              value={replyStatus}
                              onChange={(e) => setReplyStatus(e.target.value)}
                            >
                              <option value="waiting_customer">Waiting for customer</option>
                              <option value="in_progress">In progress</option>
                              <option value="resolved">Resolved</option>
                            </select>
                          </div>
                          <div className="col-md-7 d-flex gap-2 flex-wrap">
                            <button type="submit" className="btn btn-primary btn-sm" disabled={sending}>
                              {sending ? 'Sending…' : 'Send reply'}
                            </button>
                            <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => changeStatus('in_progress')}>
                              Mark in progress
                            </button>
                            <button type="button" className="btn btn-outline-success btn-sm" onClick={() => changeStatus('closed')}>
                              Close ticket
                            </button>
                          </div>
                        </div>
                      </form>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
